
import Vue from "vue"
import { EventBus } from "@/plugins/eventBus"
import { fetchUserProgress } from "@/utils/userProgress"
import type { BadgeState, UserProgressResponse } from "@/types/progression"
import { BENEFIT_ICONS, BENEFIT_SLUGS } from "@/constants/progressionActions"
import ProgressGlobalHeader from "@/components/progression/ProgressGlobalHeader.vue"
import ProgressBenefitCard from "@/components/progression/ProgressBenefitCard.vue"
import ProgressBadgeTable, { BadgeTableItem } from "@/components/progression/ProgressBadgeTable.vue"
import ProgressBadgeModal from "@/components/progression/ProgressBadgeModal.vue"
import ProgressBenefitModal from "@/components/progression/ProgressBenefitModal.vue"
import {
  badgeCtaLabel,
  benefitStatusLabel,
  formatBadgeDescription,
  formatBadgeName,
  formatBadgeProgressCount,
  formatBenefitDescription,
  formatBenefitName,
} from "@/utils/progressionI18n"
import type { TranslateFn } from "@/utils/progressionI18n"

export default Vue.extend({
  name: "Progression",
  components: {
    ProgressGlobalHeader,
    ProgressBenefitCard,
    ProgressBadgeTable,
    ProgressBadgeModal,
    ProgressBenefitModal,
  },
  data: () => ({
    progressData: null as UserProgressResponse | null,
    benefitModalOpen: false,
    badgeModalOpen: false,
    selectedBenefitSlug: "",
    selectedBadgeSlug: "",
  }),
  computed: {
    userFirstName(): string {
      return ""
    },
    globalProgress(): number {
      return this.progressData?.global_progress ?? 0
    },
    benefitCards() {
      const t: TranslateFn = (key) => this.$vuetify.lang.t(key)
      const catalog = this.progressData?.catalog.benefits || {}
      return BENEFIT_SLUGS.map((slug) => {
        const state = this.progressData?.benefits.find((b) => b.slug === slug)
        return {
          slug,
          title: formatBenefitName(t, slug, catalog[slug]?.title),
          progress: state?.progress ?? 0,
          unlocked: state?.unlocked ?? false,
          icon: BENEFIT_ICONS[slug],
          statusLabel: benefitStatusLabel(t, state?.unlocked ?? false, state?.progress ?? 0),
        }
      })
    },
    tableHeaders() {
      return [
        { text: this.$vuetify.lang.t("$vuetify.progression_table_badge"), value: "title", sortable: false },
        { text: this.$vuetify.lang.t("$vuetify.progression_table_progress"), value: "progress", sortable: false },
        { text: this.$vuetify.lang.t("$vuetify.progression_table_actions"), value: "actions_count", sortable: false },
        { text: this.$vuetify.lang.t("$vuetify.progression_table_benefits"), value: "benefits", sortable: false },
        { text: "", value: "cta", sortable: false, align: "end" },
      ]
    },
    badgeRows(): BadgeTableItem[] {
      const t: TranslateFn = (key) => this.$vuetify.lang.t(key)
      const catalog = this.progressData?.catalog.badges || {}
      return (this.progressData?.badges || []).map((badge: BadgeState) => {
        const meta = catalog[badge.slug] || {}
        return {
          slug: badge.slug,
          title: formatBadgeName(t, badge.slug, meta.title),
          description: formatBadgeDescription(t, badge.slug, meta.description),
          progress: badge.progress,
          actionsLabel: formatBadgeProgressCount(t, badge),
          benefits: badge.benefits?.length ? badge.benefits : (meta.benefits || []),
          ctaLabel: badgeCtaLabel(t, badge.progress),
        }
      })
    },
    unlockedBadgeRows(): BadgeTableItem[] {
      return this.badgeRows.filter((badge) => badge.progress < 100)
    },
    obtainedBadgeRows(): BadgeTableItem[] {
      return this.badgeRows.filter((badge) => badge.progress >= 100)
    },
    selectedBenefitTitle(): string {
      const t: TranslateFn = (key) => this.$vuetify.lang.t(key)
      return formatBenefitName(t, this.selectedBenefitSlug)
    },
    selectedBenefitDescription(): string {
      const t: TranslateFn = (key) => this.$vuetify.lang.t(key)
      const catalog = this.progressData?.catalog.benefits || {}
      return formatBenefitDescription(t, this.selectedBenefitSlug, catalog[this.selectedBenefitSlug]?.description)
    },
    benefitModalFooter(): string {
      const state = this.progressData?.benefits.find((b) => b.slug === this.selectedBenefitSlug)
      const template = this.$vuetify.lang.t("$vuetify.progression_benefit_modal_footer")
      return template.replace("{progress}", String(state?.progress ?? 0))
    },
    benefitModalBadges() {
      const t: TranslateFn = (key) => this.$vuetify.lang.t(key)
      const benefit = this.progressData?.benefits.find((b) => b.slug === this.selectedBenefitSlug)
      const required = benefit?.required_badges || []
      return required.map((slug) => {
        const badge = this.progressData?.badges.find((b) => b.slug === slug)
        return {
          slug,
          title: formatBadgeName(t, slug),
          progress: badge?.progress ?? 0,
          actionsLabel: badge
            ? formatBadgeProgressCount(t, badge)
            : formatBadgeProgressCount(t, { slug, completed_actions: 0, total_actions: 0, actions: [] }),
        }
      })
    },
  },
  created() {
    document.title = this.$vuetify.lang.t("$vuetify.tab_titles.progression")
    this.loadProgress()
  },
  methods: {
    async loadProgress() {
      try {
        this.progressData = await fetchUserProgress()
      } catch (error) {
        EventBus.$emit("snackbar", { axiosError: error })
      }
    },
    onBenefitClick(benefit: { slug: string; unlocked: boolean }) {
      if (benefit.unlocked) {
        return
      }
      this.selectedBenefitSlug = benefit.slug
      this.benefitModalOpen = true
    },
    openBadgeModal(slug: string) {
      this.selectedBadgeSlug = slug
      this.badgeModalOpen = true
    },
  },
})
